import {
  insertProgramDaySchema,
  patchProgramDaySchema,
} from "@/api/db/schema/program-days";
import {
  insertTrainingProgramSchema,
  patchTrainingProgramSchema,
} from "@/api/db/schema/training-programs";
import { programService } from "@/api/services/program.service";
import { pushSubscriptionService } from "@/api/services/push-subscription.service";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

export const createProgramFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(insertTrainingProgramSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const program = await programService.createProgram(
      data,
      context.auth.userId,
    );

    return {
      status: 201,
      body: {
        message: "Program başarıyla oluşturuldu",
        program,
      },
    };
  });

export const updateProgramFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(patchTrainingProgramSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const { programId, ...updateData } = data;
    const program = await programService.updateProgram(programId, updateData);

    if (!program) {
      return {
        status: 404,
        body: { message: "Program bulunamadı" },
      };
    }

    return {
      status: 200,
      body: {
        message: "Program güncellendi",
        program,
      },
    };
  });

const deleteProgramSchema = z.object({
  programId: z.string(),
});

export const deleteProgramFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(deleteProgramSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const program = await programService.deleteProgram(data.programId);

    if (!program) {
      return {
        status: 404,
        body: { message: "Program bulunamadı" },
      };
    }

    return {
      status: 200,
      body: { message: "Program silindi" },
    };
  });

// Program Day CRUD

export const createProgramDayFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(insertProgramDaySchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const day = await programService.createProgramDay(data);

    return {
      status: 201,
      body: {
        message: "Program günü oluşturuldu",
        day,
      },
    };
  });

export const updateProgramDayFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(patchProgramDaySchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const { dayId, ...updateData } = data;
    const day = await programService.updateProgramDay(dayId, updateData);

    if (!day) {
      return {
        status: 404,
        body: { message: "Program günü bulunamadı" },
      };
    }

    return {
      status: 200,
      body: {
        message: "Program günü güncellendi",
        day,
      },
    };
  });

const deleteProgramDaySchema = z.object({
  dayId: z.string(),
});

export const deleteProgramDayFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(deleteProgramDaySchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const day = await programService.deleteProgramDay(data.dayId);

    if (!day) {
      return {
        status: 404,
        body: { message: "Program günü bulunamadı" },
      };
    }

    return {
      status: 200,
      body: { message: "Program günü silindi" },
    };
  });

// Duplicate week
const duplicateProgramWeekSchema = z.object({
  programId: z.string(),
  sourceWeek: z.number().int().min(1),
  targetWeek: z.number().int().min(1),
});

export const duplicateProgramWeekFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(duplicateProgramWeekSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const days = await programService.cloneWeek(
      data.programId,
      data.sourceWeek,
      data.targetWeek,
    );

    return {
      status: 201,
      body: {
        message: `Hafta ${data.sourceWeek} -> Hafta ${data.targetWeek} kopyalandı`,
        days,
        total: days.length,
      },
    };
  });

// Toggle functions
const toggleSchema = z.object({
  programId: z.string(),
});

export const toggleProgramActiveFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(toggleSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const program = await programService.toggleProgramActive(data.programId);

    if (!program) {
      return {
        status: 404,
        body: { message: "Program bulunamadı" },
      };
    }

    if (program.isActive) {
      void pushSubscriptionService.sendNotificationToAllFiltered(
        "notifNewContent",
        {
          title: "Yeni Program",
          body: `"${program.name}" programı yayında!`,
          url: `/program/${program.programId}`,
        },
      );
    }

    return {
      status: 200,
      body: {
        message: `Program ${program.isActive ? "aktifleştirildi" : "devre dışı bırakıldı"}`,
        program,
      },
    };
  });

export const toggleProgramPublicFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(toggleSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const program = await programService.toggleProgramPublic(data.programId);

    if (!program) {
      return {
        status: 404,
        body: { message: "Program bulunamadı" },
      };
    }

    return {
      status: 200,
      body: {
        message: `Program ${program.isPublic ? "herkese açık" : "özel"} yapıldı`,
        program,
      },
    };
  });

export const generateProgramAccessCodeFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(toggleSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const program = await programService.generateAccessCode(data.programId);

    if (!program) {
      return {
        status: 404,
        body: { message: "Program bulunamadı" },
      };
    }

    return {
      status: 200,
      body: {
        message: "Erişim kodu oluşturuldu",
        program,
      },
    };
  });

export const clearProgramAccessCodeFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(toggleSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const program = await programService.clearAccessCode(data.programId);

    if (!program) {
      return {
        status: 404,
        body: { message: "Program bulunamadı" },
      };
    }

    return {
      status: 200,
      body: {
        message: "Erişim kodu kaldırıldı",
        program,
      },
    };
  });
